/**
 * pages/KbManagementPage.js — Phase 5A (PDF support update)
 *
 * Changes from previous version:
 *  - File input accepts .pdf in addition to .txt and .md
 *  - Drop zone label updated to show all three accepted types
 *  - File type badge shown in documents table (PDF / MD / TXT)
 *  - Error messages for scanned/encrypted PDFs shown as Alert
 *  - File size warning for PDFs > 10 MB
 *  All other behaviour unchanged.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, Button, TextField,
  MenuItem, Select, FormControl, InputLabel, Chip, IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Alert, CircularProgress, Tooltip, Divider, LinearProgress,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
} from '@mui/material';
import {
  CloudUpload, Search, DeleteOutline, DeleteForever, CheckCircle, Cancel,
  Article, PictureAsPdf, Refresh, InfoOutlined,
} from '@mui/icons-material';
import { kbApi } from '../services/hrmsApi';

// ── Constants ─────────────────────────────────────────────────────────────────

const CATEGORIES = [
  { value: 'leave_policy',  label: 'Leave Policy' },
  { value: 'compensation',  label: 'Compensation' },
  { value: 'performance',   label: 'Performance' },
  { value: 'training',      label: 'Training' },
  { value: 'compliance',    label: 'Compliance' },
  { value: 'general',       label: 'General' },
];

const ROLE_SCOPES = [
  { value: 'all',        label: 'All roles' },
  { value: 'workforce',  label: 'Workforce Pulse only' },
  { value: 'finance',    label: 'Finance & Budget only' },
  { value: 'operations', label: 'Operations only' },
  { value: 'talent',     label: 'Talent & Performance only' },
];

const CATEGORY_COLORS = {
  leave_policy: '#1565C0',
  compensation: '#00695C',
  performance:  '#AD1457',
  training:     '#E65100',
  compliance:   '#6A1B9A',
  general:      '#37474F',
};

const FILE_TYPE_CONFIG = {
  pdf: { label: 'PDF', color: '#C62828', bgcolor: '#FFEBEE' },
  md:  { label: 'MD',  color: '#1B5E20', bgcolor: '#E8F5E9' },
  txt: { label: 'TXT', color: '#37474F', bgcolor: '#ECEFF1' },
};

const MAX_PDF_SIZE_MB = 10;
const ACCEPTED_TYPES  = '.pdf,.txt,.md';

// ── Main page ─────────────────────────────────────────────────────────────────

export default function KbManagementPage() {
  const [documents,    setDocuments]    = useState([]);
  const [loadingDocs,  setLoadingDocs]  = useState(true);
  const [docsError,    setDocsError]    = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [purgeTarget,  setPurgeTarget]  = useState(null);

  const loadDocuments = useCallback(async () => {
    setLoadingDocs(true);
    setDocsError(null);
    try {
      const docs = await kbApi.listDocuments();
      setDocuments(docs || []);
    } catch {
      setDocsError('Failed to load documents. Please refresh.');
    } finally {
      setLoadingDocs(false);
    }
  }, []);

  useEffect(() => { loadDocuments(); }, [loadDocuments]);

  const handleDeactivate = async () => {
    if (!deleteTarget) return;
    try {
      await kbApi.deactivateDocument(deleteTarget.id);
      setDocuments((prev) =>
        prev.map((d) => d.id === deleteTarget.id ? { ...d, isActive: false } : d)
      );
    } finally {
      setDeleteTarget(null);
    }
  };

  const handlePurge = async () => {
    if (!purgeTarget) return;
    try {
      await kbApi.purgeDocument(purgeTarget.id);
      setDocuments((prev) => prev.filter((d) => d.id !== purgeTarget.id));
    } finally {
      setPurgeTarget(null);
    }
  };

  return (
    <Box>
      <Grid container spacing={2.5}>
        <Grid item xs={12} lg={5}>
          <UploadPanel onIngested={loadDocuments} />
        </Grid>
        <Grid item xs={12} lg={7}>
          <SearchTestPanel />
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box>
                  <Typography variant="subtitle1" fontWeight={700}>Knowledge Base Documents</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {documents.filter((d) => d.isActive).length} active ·{' '}
                    {documents.filter((d) => !d.isActive).length} inactive
                  </Typography>
                </Box>
                <Tooltip title="Refresh list">
                  <IconButton size="small" onClick={loadDocuments} disabled={loadingDocs}>
                    <Refresh fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>

              {docsError && <Alert severity="error" sx={{ mb: 2 }}>{docsError}</Alert>}

              {loadingDocs
                ? <LinearProgress sx={{ borderRadius: 1 }} />
                : <DocumentsTable documents={documents}
                    onDeactivate={(doc) => setDeleteTarget(doc)}
                    onPurge={(doc) => setPurgeTarget(doc)} />
              }
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Deactivate Document</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to deactivate <strong>{deleteTarget?.title}</strong>?
            It will be excluded from chatbot search but not deleted from the database.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button onClick={handleDeactivate} color="error" variant="contained">Deactivate</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!purgeTarget} onClose={() => setPurgeTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Document Permanently</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Permanently delete <strong>{purgeTarget?.title}</strong>? This removes the
            document and its chunks/embeddings from the vector store entirely and cannot
            be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPurgeTarget(null)}>Cancel</Button>
          <Button onClick={handlePurge} color="error" variant="contained">Delete Permanently</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ── Upload panel ──────────────────────────────────────────────────────────────

function UploadPanel({ onIngested }) {
  const fileInputRef = useRef(null);

  const [file,      setFile]      = useState(null);
  const [title,     setTitle]     = useState('');
  const [category,  setCategory]  = useState('general');
  const [roleScope, setRoleScope] = useState('all');
  const [version,   setVersion]   = useState('');
  const [uploading, setUploading] = useState(false);
  const [result,    setResult]    = useState(null);
  const [sizeWarn,  setSizeWarn]  = useState(false);

  const getFileType = (f) => {
    if (!f) return null;
    const name = f.name.toLowerCase();
    if (name.endsWith('.pdf')) return 'pdf';
    if (name.endsWith('.md'))  return 'md';
    return 'txt';
  };

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setResult(null);
    setSizeWarn(f.size > MAX_PDF_SIZE_MB * 1024 * 1024);

    if (!title) {
      const name = f.name
        .replace(/\.(pdf|md|txt)$/i, '')
        .replace(/[_-]/g, ' ');
      setTitle(name.charAt(0).toUpperCase() + name.slice(1));
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) handleFileChange({ target: { files: [f] } });
  };

  const handleSubmit = async () => {
    if (!file || !title.trim()) return;
    setUploading(true);
    setResult(null);

    const formData = new FormData();
    formData.append('file',      file);
    formData.append('title',     title.trim());
    formData.append('category',  category);
    formData.append('roleScope', roleScope);
    formData.append('version',   version.trim());

    try {
      const res = await kbApi.ingestDocument(formData);
      setResult({
        success: true,
        message: `Ingested successfully — ${res.chunkCount} chunks created.`,
      });
      setFile(null);
      setTitle('');
      setVersion('');
      setSizeWarn(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
      onIngested();
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Upload failed.';
      setResult({ success: false, message: msg });
    } finally {
      setUploading(false);
    }
  };

  const fileType   = getFileType(file);
  const isPdf      = fileType === 'pdf';
  const canSubmit  = file && title.trim() && !uploading;

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>Upload Document</Typography>
        <Typography variant="caption" color="text.secondary" display="block" mb={2}>
          Accepts .pdf, .txt, and .md files. PDFs must be text-based (not scanned images).
        </Typography>

        {/* Drop zone */}
        <Box
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          sx={{
            border: '2px dashed',
            borderColor: file ? 'success.main' : 'divider',
            borderRadius: 2, p: 3, textAlign: 'center', cursor: 'pointer',
            bgcolor: (t) => file
              ? t.palette.mode === 'dark' ? 'rgba(46,125,50,0.08)' : '#F1F8E9'
              : t.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : '#FAFAFA',
            transition: 'all 200ms ease',
            '&:hover': { borderColor: 'primary.main' },
            mb: 2,
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_TYPES}
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
          {file ? (
            <>
              {isPdf
                ? <PictureAsPdf sx={{ fontSize: 36, color: '#C62828', mb: 1 }} />
                : <Article sx={{ fontSize: 36, color: 'success.main', mb: 1 }} />
              }
              <Typography variant="body2" fontWeight={600}
                color={isPdf ? 'error.main' : 'success.main'}>
                {file.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {(file.size / 1024).toFixed(1)} KB · {fileType?.toUpperCase()} — click to change
              </Typography>
            </>
          ) : (
            <>
              <CloudUpload sx={{ fontSize: 36, color: 'text.disabled', mb: 1 }} />
              <Typography variant="body2" color="text.secondary">Drag & drop or click to select</Typography>
              <Typography variant="caption" color="text.disabled">.pdf · .txt · .md</Typography>
            </>
          )}
        </Box>

        {/* PDF size warning */}
        {sizeWarn && (
          <Alert severity="warning" sx={{ mb: 1.5, borderRadius: 2, py: 0.5, fontSize: '0.8rem' }}>
            Large PDF ({(file.size / 1024 / 1024).toFixed(1)} MB) — extraction may take a few seconds.
          </Alert>
        )}

        {/* PDF scanned-image notice */}
        {isPdf && !sizeWarn && (
          <Alert severity="info" icon={false}
            sx={{ mb: 1.5, borderRadius: 2, py: 0.5, fontSize: '0.78rem' }}>
            Text-based PDFs only. Scanned or image-only PDFs are not supported.
          </Alert>
        )}

        {/* Metadata fields */}
        <TextField label="Document Title" value={title} onChange={(e) => setTitle(e.target.value)}
          fullWidth size="small" sx={{ mb: 1.5 }} required placeholder="e.g. Annual Leave Policy" />

        <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
          <Grid item xs={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Category</InputLabel>
              <Select value={category} onChange={(e) => setCategory(e.target.value)} label="Category">
                {CATEGORIES.map((c) => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Role Scope</InputLabel>
              <Select value={roleScope} onChange={(e) => setRoleScope(e.target.value)} label="Role Scope">
                {ROLE_SCOPES.map((r) => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <TextField label="Version (optional)" value={version}
          onChange={(e) => setVersion(e.target.value)}
          fullWidth size="small" sx={{ mb: 2 }} placeholder="e.g. v3.0, Jan 2026" />

        {result && (
          <Alert severity={result.success ? 'success' : 'error'}
            icon={result.success ? <CheckCircle /> : <Cancel />}
            sx={{ mb: 2, borderRadius: 2 }}>
            {result.message}
          </Alert>
        )}

        <Button variant="contained" fullWidth onClick={handleSubmit} disabled={!canSubmit}
          startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : <CloudUpload />}
          sx={{ fontWeight: 700 }}>
          {uploading ? 'Ingesting…' : 'Ingest Document'}
        </Button>
      </CardContent>
    </Card>
  );
}

// ── Search test panel (unchanged) ─────────────────────────────────────────────

function SearchTestPanel() {
  const [query,   setQuery]   = useState('');
  const [roleScope, setRoleScope] = useState('all');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);

  const handleSearch = async () => {
    if (!query.trim()) return;
    setLoading(true); setError(null); setResults(null);
    try {
      setResults(await kbApi.testSearch(query.trim(), roleScope) || []);
    } catch {
      setError('Search failed. Ensure at least one document has been ingested.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Typography variant="subtitle1" fontWeight={700}>Test KB Search</Typography>
          <Tooltip title="Verify that ingested documents are searchable without going through the chatbot.">
            <InfoOutlined sx={{ fontSize: 16, color: 'text.disabled' }} />
          </Tooltip>
        </Box>
        <Typography variant="caption" color="text.secondary" display="block" mb={2}>
          Simulates the chatbot's knowledge base lookup.
        </Typography>

        <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
          <TextField fullWidth size="small"
            placeholder="e.g. How many days of annual leave do I get?"
            value={query} onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()} />
          <FormControl size="small" sx={{ minWidth: 140, flexShrink: 0 }}>
            <Select value={roleScope} onChange={(e) => setRoleScope(e.target.value)}>
              {ROLE_SCOPES.map((r) => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
            </Select>
          </FormControl>
          <Button variant="contained" onClick={handleSearch} disabled={!query.trim() || loading}
            startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <Search />}
            sx={{ flexShrink: 0, fontWeight: 700 }}>
            Search
          </Button>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 1.5, borderRadius: 2 }}>{error}</Alert>}

        {results !== null && (
          results.length === 0
            ? <Alert severity="warning" sx={{ borderRadius: 2 }}>
                No results found. Check chunk_count &gt; 0 in the documents table below.
              </Alert>
            : <Box>
                <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                  {results.length} chunk{results.length !== 1 ? 's' : ''} retrieved
                </Typography>
                {results.map((r, i) => (
                  <Paper key={i} variant="outlined" sx={{ p: 1.5, mb: 1, borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                      <Chip label={r.documentTitle} size="small"
                        sx={{ fontWeight: 700, fontSize: '0.72rem',
                          bgcolor: (CATEGORY_COLORS[r.category] || '#546E7A') + '18',
                          color:   CATEGORY_COLORS[r.category] || '#546E7A' }} />
                      <Chip label={r.category?.replace('_', ' ')} size="small" variant="outlined"
                        sx={{ fontSize: '0.68rem' }} />
                      <Typography variant="caption" color="text.disabled" sx={{ ml: 'auto' }}>
                        similarity: {(r.similarity * 100).toFixed(0)}%
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary"
                      sx={{ display: 'block', lineHeight: 1.6 }}>
                      {r.contentPreview}
                    </Typography>
                  </Paper>
                ))}
              </Box>
        )}
      </CardContent>
    </Card>
  );
}

// ── Documents table ───────────────────────────────────────────────────────────

function getFileType(filename) {
  if (!filename) return 'txt';
  const f = filename.toLowerCase();
  if (f.endsWith('.pdf')) return 'pdf';
  if (f.endsWith('.md'))  return 'md';
  return 'txt';
}

function DocumentsTable({ documents, onDeactivate, onPurge }) {
  if (!documents.length) {
    return (
      <Box sx={{ textAlign: 'center', py: 5, color: 'text.disabled' }}>
        <Article sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
        <Typography variant="body2">No documents ingested yet.</Typography>
        <Typography variant="caption">Upload a .pdf, .txt, or .md file above.</Typography>
      </Box>
    );
  }

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            {['Title', 'Type', 'Category', 'Role Scope', 'Version', 'Chunks', 'Ingested', 'Status', '']
              .map((h) => (
                <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                  {h}
                </TableCell>
              ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {documents.map((doc) => {
            const ft = getFileType(doc.filename);
            const ftCfg = FILE_TYPE_CONFIG[ft] || FILE_TYPE_CONFIG.txt;
            return (
              <TableRow key={doc.id} sx={{ opacity: doc.isActive ? 1 : 0.45,
                '&:last-child td': { border: 0 } }}>
                <TableCell>
                  <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: 180 }}>
                    {doc.title}
                  </Typography>
                  <Typography variant="caption" color="text.disabled" noWrap display="block">
                    {doc.filename}
                  </Typography>
                </TableCell>

                {/* File type badge */}
                <TableCell>
                  <Chip label={ftCfg.label} size="small"
                    sx={{ fontSize: '0.68rem', fontWeight: 700,
                      bgcolor: ftCfg.bgcolor, color: ftCfg.color,
                      border: `1px solid ${ftCfg.color}40` }} />
                </TableCell>

                <TableCell>
                  <Chip label={doc.category?.replace('_', ' ')} size="small"
                    sx={{ fontSize: '0.7rem', fontWeight: 600,
                      bgcolor: (CATEGORY_COLORS[doc.category] || '#546E7A') + '18',
                      color:   CATEGORY_COLORS[doc.category] || '#546E7A' }} />
                </TableCell>

                <TableCell>
                  <Typography variant="caption">{doc.roleScope}</Typography>
                </TableCell>

                <TableCell>
                  <Typography variant="caption" color="text.secondary">
                    {doc.version || '—'}
                  </Typography>
                </TableCell>

                <TableCell>
                  <Chip label={doc.chunkCount} size="small"
                    color={doc.chunkCount > 0 ? 'success' : 'error'} variant="outlined"
                    sx={{ fontSize: '0.72rem', fontWeight: 700 }} />
                </TableCell>

                <TableCell>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {doc.ingestedAt
                      ? new Date(doc.ingestedAt).toLocaleDateString('en-MY',
                          { day: '2-digit', month: 'short', year: 'numeric' })
                      : '—'}
                  </Typography>
                </TableCell>

                <TableCell>
                  <Chip label={doc.isActive ? 'Active' : 'Inactive'} size="small"
                    color={doc.isActive ? 'success' : 'default'}
                    sx={{ fontSize: '0.7rem', fontWeight: 600 }} />
                </TableCell>

                <TableCell align="right">
                  {doc.isActive && (
                    <Tooltip title="Deactivate document">
                      <IconButton size="small" onClick={() => onDeactivate(doc)}
                        sx={{ color: 'text.disabled', '&:hover': { color: 'error.main' } }}>
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {!doc.isActive && (
                    <Tooltip title="Delete permanently">
                      <IconButton size="small" onClick={() => onPurge(doc)}
                        sx={{ color: 'text.disabled', '&:hover': { color: 'error.main' } }}>
                        <DeleteForever fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
